import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { SearchRequest, SearchResponse } from '../types/search';
import type SearchService from '../services/search';
import type RouterService from '@ember/routing/router-service';

interface SearchQueryParams {
  q?: string;
  type?: string;
  page?: string;
  author?: string;
  game?: string;
  tags?: string;
}

export default class SearchRoute extends Route {
  @service declare search: SearchService;
  @service declare router: RouterService;
  @service declare head: any;

  queryParams = {
    q: { refreshModel: true },
    type: { refreshModel: false },
    page: { refreshModel: true },
    author: { refreshModel: false },
    game: { refreshModel: false },
    tags: { refreshModel: false },
  };

  async model(params: SearchQueryParams) {
    const query = params.q || '';

    // Set page metadata
    this.head.updateMeta({
      title: query
        ? `Search: ${query} | 1vX.gg`
        : 'Search | 1vX.gg',
      description: query
        ? `Search results for "${query}" on 1vX.gg - gaming news, esports coverage, and reviews`
        : 'Search 1vX.gg for gaming news, esports coverage, reviews and more',
      ogTitle: query
        ? `Search: ${query} | 1vX.gg`
        : 'Search | 1vX.gg',
      ogDescription: query
        ? `Search results for "${query}" on 1vX.gg - gaming news, esports coverage, and reviews`
        : 'Search 1vX.gg for gaming news, esports coverage, reviews and more'
    });

    // If no query, return empty state
    if (!query || query.trim().length === 0) {
      return {
        searchResponse: null,
        hasQuery: false,
        query: '',
        searchQuery: '',
        recentSearches: this.search.getRecentSearches(),
        popularSearches: [], // Could fetch from analytics
        searchResults: [],
        totalResults: 0,
        currentPage: 1,
        totalPages: 0,
        hasResults: false,
        hasError: false,
        errorMessage: '',
        searchSuggestions: [],
      };
    }

    try {
      // Build search request from query parameters
      const searchRequest: SearchRequest = {
        query: query.trim(),
        page: params.page ? parseInt(params.page, 10) : 1,
        limit: 20,
        includeHighlights: true,
        includeSuggestions: true,
        filters: this.buildFiltersFromParams(params),
      };

      // Perform search
      const searchResponse = await this.search.search(searchRequest, {
        enableAnalytics: true,
        enableCache: true,
      });

      // Build complete model with all necessary properties
      return {
        searchResponse,
        hasQuery: true,
        query,
        searchQuery: query,
        searchRequest,
        recentSearches: this.search.getRecentSearches(),
        // Extract properties from searchResponse for easy template access
        searchResults: searchResponse?.results || [],
        totalResults: searchResponse?.total || 0,
        currentPage: searchResponse?.page || 1,
        totalPages: searchResponse
          ? Math.ceil(searchResponse.total / (searchResponse.limit || 20))
          : 0,
        hasResults: searchResponse?.results?.length > 0,
        hasError: false,
        errorMessage: '',
        searchSuggestions: searchResponse?.suggestions || [],
      };
    } catch (error) {
      console.error('Search route error:', error);

      // Return error state with all necessary properties
      return {
        searchResponse: null,
        hasQuery: true,
        query,
        searchQuery: query,
        error: error instanceof Error ? error.message : 'Search failed',
        recentSearches: this.search.getRecentSearches(),
        // Error state properties
        searchResults: [],
        totalResults: 0,
        currentPage: 1,
        totalPages: 0,
        hasResults: false,
        hasError: true,
        errorMessage: error instanceof Error ? error.message : 'Search failed',
        searchSuggestions: [],
      };
    }
  }

  private buildFiltersFromParams(params: SearchQueryParams) {
    const filters: SearchRequest['filters'] = {};

    // Content type filter
    if (params.type) {
      const types = params.type
        .split(',')
        .filter((t) => ['article', 'team', 'player', 'tournament'].includes(t));
      if (types.length > 0) {
        filters.contentType = types as any[];
      }
    }

    // Author filter
    if (params.author) {
      filters.author = params.author.split(',');
    }

    // Game filter
    if (params.game) {
      filters.game = params.game.split(',');
    }

    // Tags filter
    if (params.tags) {
      filters.tags = params.tags.split(',');
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  }

  @tracked isLoading = false;

  // Actions for the route
  actions = {
    loading(transition: any) {
      this.isLoading = true;
      return true; // Bubble the loading action
    },

    didTransition() {
      this.isLoading = false;
      return true;
    },

    error(error: Error, transition: any) {
      console.error('Search route transition error:', error);
      this.isLoading = false;

      // You could redirect to an error page or handle gracefully
      return true;
    },

    // Action to handle new search from components
    performSearch(query: string, filters?: any) {
      const queryParams: Record<string, any> = { q: query };

      if (filters?.contentType?.length) {
        queryParams.type = filters.contentType.join(',');
      }
      if (filters?.author?.length) {
        queryParams.author = filters.author.join(',');
      }
      if (filters?.game?.length) {
        queryParams.game = filters.game.join(',');
      }
      if (filters?.tags?.length) {
        queryParams.tags = filters.tags.join(',');
      }

      this.router.transitionTo('search', { queryParams } as any);
    },

    // Action to handle pagination
    goToPage(page: number) {
      this.router.transitionTo('search', {
        queryParams: {
          ...this.router.currentRoute?.queryParams,
          page: page.toString(),
        },
      } as any);
    },

    // Action to clear filters
    clearFilters() {
      const currentQuery = this.router.currentRoute?.queryParams?.q || '';
      this.router.transitionTo('search', {
        queryParams: { q: currentQuery },
      } as any);
    },

    // Action to track result click
    trackResultClick(result: any, position: number) {
      const query = this.router.currentRoute?.queryParams?.q || '';
      this.search.trackResultClick(query, result, position);
    },
  };
}

declare module '@ember/routing/route' {
  interface Registry {
    search: SearchRoute;
  }
}
